<template>
	<div ref="container" class="flex items-center justify-center gap-2 sm:gap-3">
		<input type="text"
				 class="size-10 sm:size-14 rounded-md py-2 px-4 text-center font-medium sm:text-2xl text-label transition focus:ring-1 focus:ring-primary-bold-hover focus:border-primary-bold-hover disabled:cursor-default"
				 :class="props.error ? '!border-error' : ''"
				 :disabled="disabled"
				 v-for="index in indexArray" :key="index"
				 :ref="(element) => assignRef(element, index)"
				 @paste.prevent="onPaste" @focusin.prevent="onFocusIn"
				 @input.prevent="onInput(index)" @keydown="onKeyDown($event, index)"
				 :autofocus="props.autofocus && index === 0" :inputmode="props.inputMode">
	</div>
</template>

<script setup lang="js">
import { ref } from 'vue'
import { InputMode } from '@/models/enums/InputMode.js'

const props = defineProps({
	length: { type: Number, default: 6 },
	autofocus: { type: Boolean, default: true },
	disabled: { type: Boolean, default: false },
	error: { type: Boolean, default: false },
	inputClass: { type: String },
	updateImmediately: { type: Boolean, default: false },
	inputMode: { type: String, default: InputMode.Text },
	modelValue: { type: String }
})

const emit = defineEmits(['update:modelValue'])

const indexArray = [...(new Array(props.length).keys())]
const container = ref(null)
const refs = []

function assignRef(element, index)
{
	refs[index] = element
}

function onPaste(event)
{
	event.preventDefault()

	const content = event.clipboardData?.getData('text/plain')
	if (content !== undefined && content.length === props.length)
	{
		indexArray.forEach((index) => refs[index].value = content[index])
		refs[props.length - 1].focus()
		emit('update:modelValue', content)
	}
}

function onFocusIn()
{
	const firstEmpty = refs.find((element) => element.value.length === 0)
	if (firstEmpty !== undefined)
	{
		firstEmpty.focus()
	}
	else
	{
		refs[props.length - 1]?.focus()
	}
}

function onInput(index)
{
	if (index < props.length - 1)
	{
		refs[index + 1].focus()
		if (props.updateImmediately)
		{
			emit('update:modelValue', refs.map((element) => element.value).join(''))
		}
	}
	else
	{
		refs[index].value = refs[index].value.substring(0, 1)
		emit('update:modelValue', refs.map((element) => element.value).join(''))
	}
}

function onKeyDown(event, index)
{
	let code
	switch (event.key)
	{
		case 'ArrowLeft':
		case 'ArrowRight':
			event.preventDefault()
			break
		case 'Backspace':
			if (refs[index].value.length > 0)
			{
				refs[index].value = ''
			}
			else if (index > 0)
			{
				refs[index - 1].value = ''
				refs[index - 1].focus()
			}

			code = refs.map((element) => element.value).join('')
			if (code.length === 0)
			{
				code = null
			}
			if (props.updateImmediately)
			{
				emit('update:modelValue', code)
			}
			else
			{
				emit('update:modelValue', null)
			}
			break
		default:
			{
				let isNumber = !isNaN(Number(event.key))
				let isPaste = (event.ctrlKey || event.metaKey) && event.key === 'v'

				if (!isNumber && !isPaste)
				{
					event.preventDefault()
					return false
				}

				return true
			}
	}
}
</script>
